import { Tooltip } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TimeoffResponse } from 'api/timeoff/types';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import { formatTime, FormatTime } from '../format-time';

const Wrapper = styled.div`
  padding: calc(calc(1 * var(--spacing-05)) - 1px) var(--spacing-04);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
`;

const Value = styled.div``;

const TimeBarWrapper = styled(Wrapper)<{
  barPercent;
  showBorder;
  barColor;
  barHoverColor;
  barBorderColor;
  isOverCapacity;
  overCapacityColor;
  overCapacityHoverColor;
  overCapacityBorderColor;
  timeOffColor;
  value;
  isTimeOff;
  hideBackground;
}>`
  position: relative;
  ${Value} {
    visibility: hidden;
  }
  &:hover {
    &:before {
      background: ${({
        barHoverColor,
        isOverCapacity,
        overCapacityHoverColor,
        isTimeOff,
        timeOffColor,
        hideBackground,
      }) =>
        hideBackground
          ? 'transparent'
          : isTimeOff
          ? timeOffColor
          : isOverCapacity
          ? overCapacityHoverColor
          : barHoverColor};
    }
  }
  &:before {
    --bar-border-color: ${({
      barBorderColor,
      isOverCapacity,
      overCapacityBorderColor,
    }) => (isOverCapacity ? overCapacityBorderColor : barBorderColor)};
    --bar-border-width: ${({ showBorder, isTimeOff, hideBackground }) =>
      showBorder && !isTimeOff && !hideBackground ? '1px' : '0'};
    content: '';
    position: absolute;
    background: ${({
      barColor,
      isOverCapacity,
      overCapacityColor,
      isTimeOff,
      timeOffColor,
      hideBackground,
    }) =>
      hideBackground
        ? 'transparent'
        : isTimeOff
        ? timeOffColor
        : isOverCapacity
        ? overCapacityColor
        : barColor};
    bottom: 0;
    width: 100%;
    left: 0;
    border-top: var(--bar-border-width) solid var(--bar-border-color);
    max-height: ${({ barPercent, isTimeOff }) =>
      `${isTimeOff ? '100' : barPercent}%`};
    animation: grow 0.5s cubic-bezier(0, 0.55, 0.45, 1) forwards;
  }

  @keyframes grow {
    0% {
      height: 0;
    }
    100% {
      height: 100%;
    }
  }
  &:after {
    content: ${({ value }) => `"${value}"`};
    position: absolute;
    top: 0;
    left: 0;
    padding: var(--spacing-05);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
`;

interface Props {
  minutes: number;
  capacity: number;
  isTimeOff?: TimeoffResponse | undefined;
  showBorder?: boolean;
  barColor?: string;
  barBorderColor?: string;
  overCapacityColor?: string;
  overCapacityBorderColor?: string;
  barHoverColor?: string;
  overCapacityHoverColor?: string;
  timeOffColor?: string;
  className?: string;
  userName?: string;
  hideBackground?: boolean;
  onUserSelection?: () => void;
}
export const TimeBarCell: FC<Props> = ({
  minutes,
  capacity,
  isTimeOff,
  userName,
  hideBackground = false,
  showBorder = true,
  barColor = 'var(--green-10)',
  barHoverColor = 'var(--green-20)',
  barBorderColor = 'var(--green-40)',
  overCapacityColor = 'var(--red-20)',
  overCapacityHoverColor = 'var(--red-30)',
  overCapacityBorderColor = 'var(--red-50)',
  timeOffColor = 'var(--purple-20)',
  className,
  onUserSelection,
}) => {
  const utilizedPercent =
    capacity === 0 ? 100 : (Math.min(minutes, capacity) / capacity) * 100;
  const barPercent = minutes === 0 ? 0 : utilizedPercent;
  const _isOverCapacity = minutes > capacity;
  const _minutesDifference = _isOverCapacity
    ? minutes - capacity
    : capacity - minutes;

  const timeOffTooltipContent = (
    <GetTimeOffTooltipContent timeOff={isTimeOff} userName={userName} />
  );

  const _tooltipText =
    _minutesDifference !== 0 ? (
      _isOverCapacity ? (
        <FormattedMessage
          id="timesheets.text.over_capacity"
          values={{ time: formatTime(_minutesDifference) }}
        />
      ) : (
        <FormattedMessage
          id="timesheets.text.under_capacity"
          values={{ time: formatTime(_minutesDifference) }}
        />
      )
    ) : (
      ''
    );

  return (
    <Tooltip title={isTimeOff ? timeOffTooltipContent : _tooltipText}>
      <TimeBarWrapper
        barPercent={barPercent}
        showBorder={barPercent && showBorder}
        barColor={barColor}
        isOverCapacity={_isOverCapacity}
        overCapacityColor={overCapacityColor}
        barBorderColor={barBorderColor}
        overCapacityBorderColor={overCapacityBorderColor}
        overCapacityHoverColor={overCapacityHoverColor}
        barHoverColor={barHoverColor}
        className={className}
        value={formatTime(minutes)}
        timeOffColor={timeOffColor}
        isTimeOff={isTimeOff}
        hideBackground={hideBackground}
        onClick={() => onUserSelection && onUserSelection()}
      >
        <Value>
          <FormatTime value={minutes} showHypenIfZero={false} />
        </Value>
      </TimeBarWrapper>
    </Tooltip>
  );
};

const TotalTimeWrapper = styled(Wrapper)`
  font-weight: var(--font-weight-bold);
`;

interface TotalTimeProps {
  minutes: number;
  className?: string;
}
export const TotalTimeCell: FC<TotalTimeProps> = ({ minutes, className }) => (
  <TotalTimeWrapper className={className}>
    <FormatTime value={minutes} showHypenIfZero={false} />
  </TotalTimeWrapper>
);
